import React from 'react';
import { COLORS, date, dateTime, money, Spinner, Table } from '../ui';

/**
 * The detail drawer: slides in from the right whenever a tile, stage segment or
 * row is clicked.
 *
 * It holds the list behind the number that was clicked, and drills to one work
 * order from there. The list always comes from the same endpoint that produced
 * the number, so what is shown can never disagree with the figure that led here.
 */

const RAG = { red: COLORS.critical, amber: COLORS.warning, green: COLORS.good };

export function Rag({ value }) {
    return <span style={{
        display: 'inline-block', width: 9, height: 9, borderRadius: '50%',
        background: RAG[value] || COLORS.muted,
    }} />;
}

const pct = v => v == null ? '—' : `${Math.round(v * 100)}%`;
const days = v => v == null ? '—' : `${v.toFixed(1)}d`;

function Row({ label, children }) {
    if (children == null || children === '') return null;
    return (
        <div style={{ display: 'flex', gap: 10, padding: '5px 0', borderBottom: `1px solid ${COLORS.grid}` }}>
            <div style={{ width: 130, flex: '0 0 130px', fontSize: 11.5, color: COLORS.muted, fontWeight: 600 }}>{label}</div>
            <div style={{ fontSize: 12.5, color: COLORS.ink, minWidth: 0, wordBreak: 'break-word' }}>{children}</div>
        </div>
    );
}

/** One work order in full, including the stage clocks that put it on the list. */
function Detail({ wo, onBack }) {
    const mcUrl = `http://mc.unitedpetroleum.com.au/MainPage.aspx?WOID=${encodeURIComponent(wo.id)}`;
    return (
        <div>
            <button onClick={onBack} style={linkButton}>← Back to list</button>
            <h3 style={{ margin: '10px 0 4px', fontSize: 16 }}>{wo.id}</h3>
            <div style={{ fontSize: 12.5, color: COLORS.ink2, marginBottom: 12 }}>
                {wo.site} ({wo.shopId}) · {wo.state} · {wo.stream}
            </div>

            <Row label="Status">{wo.status}{wo.subStatus ? ` — ${wo.subStatus}` : ''}</Row>
            <Row label="Stage">{wo.stage ? `${wo.stage} (${days(wo.stageDays)})` : '—'}</Row>
            <Row label="Type">{wo.type}{wo.isFollowup ? ' · follow-up' : ''}</Row>
            <Row label="Priority">{wo.priorityDesc || wo.priority}</Row>
            <Row label="Contractor">{wo.contractor || '—'}</Row>
            <Row label="Issued by">{wo.person || '—'}</Row>
            <Row label="Requested">{dateTime(wo.requestedDate)}</Row>
            <Row label="Issued">{dateTime(wo.issuedDate)}</Row>
            <Row label="Approved">{dateTime(wo.approvedDate)}</Row>
            <Row label="Completed">{dateTime(wo.completeDate)}</Row>
            <Row label="Closed">{dateTime(wo.closedDate)}</Row>
            <Row label="Dwell">{days(wo.dwellDays)}</Row>
            <Row label="Age">{wo.ageDays == null ? '—' : `${Math.round(wo.ageDays)} days`}</Row>
            <Row label="SLA consumed">{wo.slaPct == null ? '—' : pct(wo.slaPct)}</Row>
            <Row label="SLA due">{dateTime(wo.slaDueAt)}</Row>
            <Row label="Last note">
                {wo.lastNoteDate ? `${date(wo.lastNoteDate)} (${Math.round(wo.daysSinceNote)}d ago)` : 'never'}
            </Row>
            <Row label="Est. cost">{wo.costEstimated ? money(wo.costEstimated) : null}</Row>
            <Row label="Parent WO">{wo.parentPk || null}</Row>
            <Row label="Reason">{(wo.reason || '').replace(/%0D%0A/g, ' ').slice(0, 600)}</Row>

            <div style={{ marginTop: 14, display: 'flex', gap: 10 }}>
                <a href={mcUrl} target="_blank" rel="noreferrer" style={{ ...linkButton, textDecoration: 'none' }}>
                    Open in Maintenance Connection
                </a>
                <a href={`/wo/${wo.pk}`} style={{ ...linkButton, textDecoration: 'none' }}>Full record</a>
            </div>
        </div>
    );
}

const linkButton = {
    background: 'none', border: `1px solid ${COLORS.link}`, color: COLORS.link,
    padding: '4px 10px', borderRadius: 4, cursor: 'pointer', fontSize: 12,
    display: 'inline-block',
};

export default function WoDrawer({ open, title, subtitle, loading, items, total, truncated, onClose, onExport }) {
    const [selected, setSelected] = React.useState(null);

    // A new list means the previously selected row is gone; showing its detail
    // over an unrelated list is worse than showing nothing.
    React.useEffect(() => { setSelected(null); }, [title, items]);

    // Escape closes, which is what anyone who has used a drawer will try first.
    React.useEffect(() => {
        if (!open) return undefined;
        const onKey = e => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, onClose]);

    if (!open) return null;

    const columns = [
        { key: 'id', label: 'WO', render: r => <span style={{ fontWeight: 600 }}>{r.id}</span> },
        { key: 'site', label: 'Site', render: r => `${r.site || ''}${r.state ? ` (${r.state})` : ''}` },
        { key: 'stream', label: 'Stream' },
        { key: 'status', label: 'Status' },
        { key: 'person', label: 'Issued by', render: r => r.person || '—' },
        { key: 'contractor', label: 'Contractor', render: r => (r.contractor || '—').slice(0, 28) },
        { key: 'requestedDate', label: 'Requested', render: r => date(r.requestedDate) },
        { key: 'ageDays', label: 'Age', align: 'right', render: r => r.ageDays == null ? '—' : `${Math.round(r.ageDays)}d` },
        { key: 'dwellDays', label: 'Dwell', align: 'right', render: r => days(r.dwellDays) },
    ];

    return (
        <>
            <div onClick={onClose} style={{
                position: 'fixed', inset: 0, background: 'rgba(11,11,11,0.28)', zIndex: 40,
            }} />
            <aside style={{
                position: 'fixed', top: 0, right: 0, bottom: 0, width: 'min(980px, 94vw)',
                background: COLORS.surface, zIndex: 41, boxShadow: '-6px 0 24px rgba(0,0,0,0.18)',
                display: 'flex', flexDirection: 'column',
            }}>
                <header style={{
                    display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px',
                    borderBottom: `1px solid ${COLORS.grid}`, background: COLORS.plane,
                }}>
                    <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 15, fontWeight: 700 }}>{title}</div>
                        {subtitle && <div style={{ fontSize: 12, color: COLORS.muted }}>{subtitle}</div>}
                    </div>
                    <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                        {onExport && items?.length > 0 && (
                            <button onClick={onExport} style={linkButton}>Export to Excel</button>
                        )}
                        <button onClick={onClose} style={linkButton}>Close</button>
                    </div>
                </header>

                <div style={{ overflow: 'auto', padding: 16, flex: 1 }}>
                    {loading && <Spinner label="Loading work orders" />}
                    {!loading && selected && <Detail wo={selected} onBack={() => setSelected(null)} />}
                    {!loading && !selected && (
                        <>
                            {truncated && (
                                <div style={{ fontSize: 12, color: COLORS.muted, marginBottom: 8 }}>
                                    Showing the oldest {items.length} of {total}.
                                </div>
                            )}
                            <Table
                                columns={columns}
                                rows={items || []}
                                rowKey={r => r.pk}
                                maxHeight="calc(100vh - 170px)"
                                onRowClick={setSelected}
                                empty="No work orders in this selection."
                            />
                        </>
                    )}
                </div>
            </aside>
        </>
    );
}
